import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch } from "@/lib/api";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";

/**
 * Customers — the tenant orgs nested under the current account.
 *
 * "Customer" is the product word; the API models them as orgs whose `parent_id`
 * is the current org. That is the same query the Customers page issues
 * (pages/admin/TenantsPage.tsx), lifted into a hook so surfaces other than that
 * page — the command palette — can read the list without re-implementing the
 * parent filter and getting a different set.
 *
 * Parentage alone, deliberately: `?tier=tenant` used to be sent alongside it and
 * hid real customers. That filter resolves to `type IN (end_org) OR (personal
 * AND nested)`, so a child org created with any other type — which is what
 * POST /orgs writes when a platform_admin omits `type`, since it defaults to
 * `company` for that caller — was silently absent from every customer surface
 * while showing up in lists that do not filter on tier. A child of this account
 * IS this account's customer, whatever its type; nothing about the tier filter
 * narrowed the set correctly, it only dropped rows.
 */

export interface Customer {
  id: string;
  name: string;
  slug: string;
  status: string;
  /** The customer tier this customer is on, or null for none. Comes off the
   *  list so Billing can count customers per tier without a config read each. */
  tierId: string | null;
}

function mapCustomer(t: Record<string, unknown>): Customer {
  return {
    id:     (t.id as string) ?? "",
    name:   (t.name as string) ?? "",
    slug:   (t.slug as string) ?? "",
    status: (t.status as string) ?? "active",
    tierId: (t.tierId as string | null) ?? null,
  };
}

interface UseCustomersOptions {
  /** Leave the query idle until the caller needs it (e.g. a closed dialog). */
  enabled?: boolean;
}

export function useCustomers({ enabled = true }: UseCustomersOptions = {}) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.customers.list(orgId),
    queryFn: async () => {
      // `enabled` below keeps this from running without an orgId, so the
      // request is always scoped to one parent.
      const params = new URLSearchParams({ parent_id: orgId ?? "" });
      // The endpoint returns either a bare array or `{ data: [...] }` depending
      // on the caller's scope, so both shapes are unwrapped here rather than at
      // every call site.
      const raw = await apiFetch<Record<string, unknown>[] | { data?: Record<string, unknown>[] }>(
        `${API_BASE}/v1/orgs?${params}`,
      );
      const list = Array.isArray(raw) ? raw : raw?.data ?? [];
      return list.map(mapCustomer).filter((c) => c.id && c.name);
    },
    enabled: enabled && !!orgId,
  });
}
